import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { readFileAsDataUrl } from "@/lib/cropImage";
import { fotoUrl } from "@/lib/user";
import ImageCropDialog from "@/components/ImageCropDialog";
import { Button } from "@/components/ui/button";

const MAX_BYTES = 2 * 1024 * 1024;

export default function FotoTrabajadorEditor({
  fotoUrlProp = null,
  uploadUrl,
  deleteUrl = null,
  onUpdated,
  size = "lg",
  editable = true,
  cacheKey = null,
}) {
  const inputRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [preview, setPreview] = useState(fotoUrlProp);
  const [version, setVersion] = useState(cacheKey ?? Date.now());

  useEffect(() => {
    setPreview(fotoUrlProp);
  }, [fotoUrlProp]);

  const boxClass = size === "lg" ? "h-16 w-16 rounded-2xl" : "h-12 w-12 rounded-xl";
  const iconClass = size === "lg" ? "h-7 w-7" : "h-6 w-6";
  const src = preview ? fotoUrl({ foto_url: preview }, version) : null;

  const abrirSelector = () => inputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen JPG, PNG o WEBP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("La imagen no puede superar 2 MB.");
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropSrc(dataUrl);
      setCropOpen(true);
    } catch {
      toast.error("No se pudo leer la imagen.");
    }
  };

  const subirBlob = async (blob) => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("foto", blob, "foto.jpg");
      const { data } = await api.post(uploadUrl, fd);
      const nueva = data.foto_url || null;
      setPreview(nueva);
      setVersion(Date.now());
      onUpdated?.(nueva);
      toast.success("Foto actualizada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubiendo(false);
    }
  };

  const quitarFoto = async () => {
    if (!deleteUrl) return;
    setSubiendo(true);
    try {
      await api.delete(deleteUrl);
      setPreview(null);
      setVersion(Date.now());
      onUpdated?.(null);
      toast.success("Foto eliminada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className={`relative flex shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md ${boxClass}`}>
        {src ? (
          <img src={src} alt="" className="h-full w-full object-cover" />
        ) : (
          <User className={iconClass} strokeWidth={1.5} />
        )}
        {subiendo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-5 w-5 animate-spin text-white" />
          </div>
        )}
      </div>

      {editable && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={abrirSelector} disabled={subiendo}>
            <Camera className="mr-1.5 h-4 w-4" />
            {src ? "Cambiar foto" : "Añadir foto"}
          </Button>
          {src && deleteUrl && (
            <Button type="button" variant="ghost" size="sm" onClick={quitarFoto} disabled={subiendo}>
              <Trash2 className="mr-1.5 h-4 w-4" />
              Quitar
            </Button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onFileChange}
            data-testid="foto-trabajador-input"
          />
        </div>
      )}

      <ImageCropDialog
        open={cropOpen}
        imageSrc={cropSrc}
        onClose={() => {
          setCropOpen(false);
          setCropSrc(null);
        }}
        onConfirm={subirBlob}
      />
    </div>
  );
}
